/**
 * The one place overlays live, and the machinery that makes a modal actually
 * modal.
 *
 * Two components portal here — Dialog and Toast — and they have to share a host
 * for one specific reason: **a dialog makes the page behind it inert, and a
 * toast fired while a dialog is open still has to be announced.** If the toast
 * host were an ordinary child of `<body>`, hiding the background from assistive
 * technology would hide the live region with it, and every error raised from
 * inside a dialog would go unspoken. Both live in this root; the root is never
 * hidden.
 *
 * Why portal at all: a `position: fixed` element is positioned against the
 * nearest ancestor with a `transform`, `filter` or `contain`, not against the
 * viewport, so a dialog rendered in place is one CSS property away from being
 * clipped inside a card. And an `overflow: hidden` ancestor — which the shell's
 * scroll regions have — would cut it off outright.
 *
 * Nothing here is React. It is DOM state that outlives any single component,
 * which is exactly why it is not a hook: two dialogs, mounted independently,
 * have to agree on one stack, one scroll lock and one snapshot of what was
 * already `aria-hidden`.
 */

const OVERLAY_ROOT_ATTRIBUTE = 'data-overlay-root';

/**
 * The shared, permanently-mounted host. Created on first use, never removed.
 *
 * The DOCUMENT is the source of truth, not a module variable. An earlier
 * version cached the node and kept this query as a fallback, and mutation
 * testing showed the two masked each other perfectly: breaking either one left
 * the other to find the same node, so neither could be killed by any test. Two
 * paths that cannot be told apart are one path with a spare. The query also
 * self-heals across a hot reload or a document reset, which the cache could not.
 */
export function getOverlayRoot(): HTMLElement {
  const existing = document.querySelector<HTMLElement>(`[${OVERLAY_ROOT_ATTRIBUTE}]`);
  if (existing !== null) return existing;

  const root = document.createElement('div');
  root.setAttribute(OVERLAY_ROOT_ATTRIBUTE, '');
  document.body.appendChild(root);
  return root;
}

/* ------------------------------------------------------- the modal stack */

const stack: HTMLElement[] = [];

/** Snapshot of what `aria-hidden` said before the first dialog opened. */
const hiddenBefore = new Map<Element, string | null>();
let overflowBefore: string | null = null;

function hideBackground(): void {
  const root = getOverlayRoot();
  for (const child of Array.from(document.body.children)) {
    if (child === root) continue;
    hiddenBefore.set(child, child.getAttribute('aria-hidden'));
    child.setAttribute('aria-hidden', 'true');
  }
}

function showBackground(): void {
  for (const [child, previous] of hiddenBefore) {
    if (previous === null) child.removeAttribute('aria-hidden');
    else child.setAttribute('aria-hidden', previous);
  }
  hiddenBefore.clear();
}

/**
 * Registers an open dialog. The first one hides the page from assistive
 * technology and stops it scrolling; later ones only join the stack, so a
 * confirmation opened from inside another dialog does not unlock the page when
 * it closes.
 */
export function pushDialog(panel: HTMLElement): void {
  if (stack.includes(panel)) return;
  if (stack.length === 0) {
    overflowBefore = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    hideBackground();
  }
  stack.push(panel);
}

export function popDialog(panel: HTMLElement): void {
  const index = stack.indexOf(panel);
  if (index === -1) return;
  stack.splice(index, 1);
  if (stack.length > 0) return;

  // Restored to what it WAS. Blanking it would silently take ownership of a
  // property the application shell may be setting for its own reasons.
  document.body.style.overflow = overflowBefore ?? '';
  overflowBefore = null;
  showBackground();
}

/**
 * Only the topmost dialog answers Escape and only the topmost traps Tab.
 * Without this, one Escape closes every dialog in the stack at once and the
 * focus trap of a dialog nobody can see fights the one they are looking at.
 */
export function isTopmostDialog(panel: HTMLElement): boolean {
  return stack.length > 0 && stack[stack.length - 1] === panel;
}
